import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Plus, X, Edit2, Trash2,
  Search, Eye, Clock,
  CheckCircle, AlertCircle,
  Loader,
  AlertTriangle, BarChart, Database,
  FileCheck, Save, ChevronUp, ChevronDown, ChevronRight, ChevronLeft, Users, Calendar, PanelLeftClose, PanelLeft, Info,
  FilePlus2, Archive, BookOpenCheck
} from 'lucide-react';
import * as mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import MasterData from '../../../pages/MasterData';
import RuleBook from '../rule-book/RuleBook';
import { ContractReview } from '../review/ContractReview';
import ContractReviewPage from '../review/ContractReviewPage';
import ContractDetailsModal from './ContractDetailsModal';
import { useAuth } from '../../../contexts/AuthContext';
import UserDropdown from '../auth/UserDropdown';
import { PermissionGuard } from '../../../hooks/usePermissions';
import { ContractRepository } from '../repository/ContractRepository';
import type { ContractCategory } from '../../../services/api/contractService';
import { CONTRACT_CATEGORIES } from '../../../utils/contractCategories';
import {
  Contract as ContractType,
  Supplier as SupplierType,
  ReviewClause,
  NegotiationPoint as NegotiationPointType,
  AnalysisDocument,
  ContractSummary,
  Attachment as AttachmentType,
  ContractAnalysisJSON
} from '../../../types/contract.types';
import { formatRelativeTime } from '../../../utils/timeUtils';
import { decodeHtmlEntities } from '../../../utils/textUtils';
import { parseContractAnalysis } from '../../../services/parsers/contractAnalysisParser';
import { contractService, supplierService, contractGroupService, ContractApiResponse, ContractGroup } from '../../../services/api';

// Set the worker source for PDF.js to use local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Extended Contract type for backward compatibility
interface Contract extends Omit<ContractType, 'contractTitle' | 'supplier' | 'value' | 'category'> {
  _id?: string; // MongoDB ObjectId for backend operations
  supplierId: string | number;
  supplierName: string;
  contractTitle: string;
  year: number;
  negotiationPoints: NegotiationPointType[];
  attachments?: AttachmentType[];
  paymentTerms?: string;
  renewalDate?: string;
  // New fields
  minPurchaseAmount?: number;
  marketingBudget?: number;
  renewalTermsCount?: number;
  incotermTerms?: string;
  paymentCurrency?: string;
  groupId?: string;
  groupName?: string;
  category?: string | null;
  // Negotiation email generated after review
  negotiationEmail?: {
    subject: string;
    body: string;
    generatedAt: string;
    generatedBy?: string;
  };
}

interface Supplier extends SupplierType {}

interface ContractFormData {
  supplierId: string | number;
  contractTitle: string;
  year: number;
  startDate: string;
  endDate: string;
  status: 'draft' | 'active' | 'expired' | 'terminated';
  reviewStatus: 'not_received' | 'pending_review' | 'under_review' | 'finalized';
  description: string;
  paymentTerms: string;
  renewalDate: string;
  attachments: AttachmentType[];
  negotiationPoints: NegotiationPointType[];
  // New fields
  minPurchaseAmount?: number;
  marketingBudget?: number;
  renewalTermsCount?: number;
  incotermTerms?: string;
  paymentCurrency?: string;
  groupId?: string;
  category?: ContractCategory | null;
}

// Status options configuration
const statusOptions = [
  { value: 'draft', label: 'Draft', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  { value: 'terminated', label: 'Terminated', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
];

const reviewStatusOptions = [
  { value: 'not_received', label: 'Not Received', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
  { value: 'pending_review', label: 'Pending Review', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'under_review', label: 'Under Review', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'finalized', label: 'Finalized', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
];

// Clickable Status Dropdown Component
interface StatusDropdownProps {
  contractId: string;
  currentStatus: string;
  type: 'status' | 'reviewStatus';
  onUpdate: (contractId: string, field: string, value: string) => Promise<void>;
  disabled?: boolean;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ contractId, currentStatus, type, onUpdate, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const options = type === 'status' ? statusOptions : reviewStatusOptions;
  const currentOption = options.find(opt => opt.value === currentStatus) || options[0];

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (value: string) => {
    if (value === currentStatus || disabled || isUpdating) return;

    setIsUpdating(true);
    try {
      await onUpdate(contractId, type, value);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => !disabled && !isUpdating && setIsOpen(!isOpen)}
        disabled={disabled || isUpdating}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-all ${currentOption.color} ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-300 dark:hover:ring-gray-600'
        }`}
      >
        {isUpdating ? (
          <Loader className="w-3 h-3 animate-spin" />
        ) : null}
        {currentOption.label}
        {!disabled && !isUpdating && (
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-36 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border py-1 left-0">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 ${
                option.value === currentStatus ? 'bg-gray-50 dark:bg-gray-800' : ''
              }`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${option.color.split(' ')[0]}`} />
              <span className="text-gray-700 dark:text-gray-300">{option.label}</span>
              {option.value === currentStatus && (
                <CheckCircle className="w-3.5 h-3.5 ml-auto text-green-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ContractManager: React.FC = () => {
  // Helper functions for mapping JSON analysis to internal formats
  const mapClassificationToCategory = (classification: string): string => {
    switch (classification) {
      case 'deal-breaker':
      case 'dealbreaker':
        return 'Critical';
      case 'negotiable':
        return 'Important';
      case 'acceptable':
        return 'Standard';
      default:
        return 'Other';
    }
  };

  const mapRiskToPriority = (riskLevel: string): 'high' | 'medium' | 'low' => {
    const risk = riskLevel.toLowerCase();
    if (risk.includes('high')) return 'high';
    if (risk.includes('medium')) return 'medium';
    return 'low';
  };

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return '';
    }
  };

  // Generate consistent numeric IDs from ObjectIds to avoid collisions
  const generateNumericId = (objectId: string): number => {
    if (!objectId) return 0;
    // Use a simple hash of the ObjectId to generate a unique number
    let hash = 0;
    for (let i = 0; i < objectId.length; i++) {
      const char = objectId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const currentUser = user ? `${user.firstName} ${user.lastName}` : 'Guest';
  
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [endDateFilter, setEndDateFilter] = useState('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [contractGroups, setContractGroups] = useState<ContractGroup[]>([]);
  const [startDateFrom, setStartDateFrom] = useState('');
  const [startDateTo, setStartDateTo] = useState('');
  const [sortField, setSortField] = useState<'contractTitle' | 'supplierName' | 'groupName' | 'status' | 'reviewStatus' | 'endDate'>('contractTitle');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Keep selectedContract in sync with the latest contract data (e.g., when negotiationEmail is loaded)
  useEffect(() => {
    if (selectedContract && contracts.length > 0) {
      const updatedContract = contracts.find(c => c.id === selectedContract.id);
      if (updatedContract && updatedContract !== selectedContract) {
        // Only update if data actually changed (e.g., negotiationEmail added)
        if (JSON.stringify(updatedContract) !== JSON.stringify(selectedContract)) {
          setSelectedContract(updatedContract);
        }
      }
    }
  }, [contracts, selectedContract]);
  const [reviewClauses, setReviewClauses] = useState<ReviewClause[]>([]);
  const [negotiationPoints, setNegotiationPoints] = useState<NegotiationPointType[]>([]);
  const [, setProcessingStep] = useState('');
  
  // Utility: basic MongoDB ObjectId validator
  const isValidObjectId = (value: string): boolean => /^[a-fA-F0-9]{24}$/.test(value);
  const [contractFormData, setContractFormData] = useState<ContractFormData>({
    supplierId: '',
    contractTitle: '',
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    status: 'draft',
    reviewStatus: 'not_received',
    description: '',
    paymentTerms: '',
    renewalDate: '',
    attachments: [],
    negotiationPoints: [],
    // New fields
    minPurchaseAmount: undefined,
    marketingBudget: undefined,
    renewalTermsCount: undefined,
    incotermTerms: '',
    paymentCurrency: '',
    groupId: undefined,
    category: null
  });
  const [supplierForm, setSupplierForm] = useState<Supplier>({
    id: 0,
    companyName: '',
    taxId: '',
    registrationNumber: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    contactPersons: [{
      name: '',
      role: '',
      email: '',
      phone: '',
      isPrimary: true
    }],
    category: 'Technology',
    status: 'active'
  });

  // Commented out unused state (used in commented function _handleAddNegotiationPointOld)
  /* const [newNegotiationPoint, setNewNegotiationPoint] = useState({
    category: '',
    point: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
    status: 'pending' as 'pending' | 'agreed' | 'rejected' | 'compromised'
  }); */

  // Removed unused negotiationCategories and priorityColors

  // Utility functions for notifications
  const showError = (message: string) => {
    alert(`❌ ${message}`);
  };

  const showSuccess = (message: string) => {
    alert(`✅ ${message}`);
  };

  // Contract Review Handlers
  const handleAnalysisUpload = async (
    analysisResult: any,
    selectedContractId: string,
    file: File,
    isJsonAnalysis?: boolean,
    jsonData?: ContractAnalysisJSON
  ) => {
    try {
      setProcessingStep('Processing analysis results...');
      
      if (!selectedContractId || selectedContractId === '0' || selectedContractId === '') {
        showError('Please select a contract before uploading analysis');
        return;
      }

      const selectedContract = contracts.find(c => c.id === selectedContractId);
      if (!selectedContract) {
        showError('Selected contract not found');
        return;
      }

      // Handle JSON analysis
      if (jsonData || analysisResult.jsonAnalysis) {
        const analysis = jsonData || analysisResult.jsonAnalysis;
        
        // Update the contract with the new JSON analysis
        const updatedContracts = contracts.map(contract => {
          if (contract.id === selectedContractId) {
            return {
              ...contract,
              jsonAnalysis: analysis,
              hasJsonAnalysis: true,
              summary: analysis.executive_summary.overview,
              // Map summary fields for all clauses
              clauses: analysis.clauses.map((clause: any, index: number) => ({
                clauseId: `clause_${selectedContractId}_${index}`,
                clauseTitle: clause.title,
                clauseContent: clause.party_obligations,
                clauseSummary: clause.summary || clause.party_obligations.slice(0, 150) + '...',
                category: mapClassificationToCategory(clause.negotiation_analysis.classification),
                priority: mapRiskToPriority(clause.risk_assessment.legal_risk),
                status: 'pending',
                comments: '',
                reviewedBy: '',
                reviewDate: new Date(),
                negotiationHistory: [],
                // Include all the enhanced fields
                reference: clause.reference,
                fullHeading: clause.full_heading,
                problematicSubclauses: clause.problematic_subclauses,
                riskAssessment: clause.risk_assessment,
                negotiationAnalysis: clause.negotiation_analysis,
                actionableRecommendations: clause.actionable_recommendations,
                icons: clause.icons
              }))
            };
          }
          return contract;
        });
        
        setContracts(updatedContracts);

        showSuccess('Contract analysis uploaded successfully with enhanced format!');
        setProcessingStep('');
        return;
      }

      // Read file content based on type
      let fileContent = '';
      
      if (file.type === 'text/plain' || file.type === 'text/markdown' || file.name.endsWith('.md')) {
        // Handle text files
        fileContent = await file.text();
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.endsWith('.docx')) {
        // Handle DOCX files using mammoth
        try {
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          fileContent = result.value;
          
          if (!fileContent || fileContent.trim().length === 0) {
            throw new Error('Could not extract text from DOCX file. The file may be empty or corrupted.');
          }
        } catch (docxError: any) {
          throw new Error(`DOCX processing error: ${docxError.message || 'Failed to extract text from DOCX file'}`);
        }
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        // Handle PDF files using pdfjs-dist
        try {
          const arrayBuffer = await file.arrayBuffer();
          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;
          
          let fullText = '';
          // Extract text from all pages
          for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items
              .map((item: any) => item.str)
              .join(' ');
            fullText += pageText + '\n';
          }
          
          fileContent = fullText;
          
          if (!fileContent || fileContent.trim().length === 0) {
            throw new Error('Could not extract text from PDF file. The file may be empty, corrupted, or contain only images.');
          }
        } catch (pdfError: any) {
          console.error('PDF processing error details:', pdfError);
          if (pdfError.message && pdfError.message.includes('worker')) {
            throw new Error('PDF processing error: PDF.js worker failed to load. Please check your internet connection or try again.');
          }
          throw new Error(`PDF processing error: ${pdfError.message || 'Failed to extract text from PDF file'}`);
        }
      } else {
        throw new Error(`Unsupported file type: ${file.type}. Please use TXT, MD, PDF, DOCX, or convert your document to a supported format.`);
      }

      if (!fileContent) {
        throw new Error('Unable to read file content. Please ensure the file is not empty.');
      }

      // Use enhanced parser
      const parsedAnalysis = parseContractAnalysis(fileContent);

      // Update contract with favorability rating
      if (parsedAnalysis.favorabilityRating) {
        setContracts(prevContracts => 
          prevContracts.map(c => 
            c.id === selectedContractId 
              ? { ...c, favorabilityRating: parsedAnalysis.favorabilityRating }
              : c
          )
        );
      }

      // Update contract with executive summary
      if (parsedAnalysis.executiveSummary) {
        const executiveSummaryWithId = {
          ...parsedAnalysis.executiveSummary,
          contractId: selectedContractId
        };
        
        setContracts(prevContracts => 
          prevContracts.map(c => 
            c.id === selectedContractId 
              ? { ...c, executiveSummary: executiveSummaryWithId }
              : c
          )
        );
      }

      // Update contract summary
      handleSummaryUpdate(selectedContractId, {
        generatedSummary: parsedAnalysis.favorabilityRating.executiveSummary || '',
        keyFindings: parsedAnalysis.favorabilityRating.criticalIssues?.map(issue => issue.title) || [],
        riskLevel: parsedAnalysis.favorabilityRating.overallRating < 5 ? 'high' : 
                   parsedAnalysis.favorabilityRating.overallRating < 7 ? 'medium' : 'low',
        createdDate: new Date(),
        createdBy: currentUser
      });

      // Add enhanced clauses
      if (parsedAnalysis.clauses && parsedAnalysis.clauses.length > 0) {
        // Process clauses with a small delay to ensure unique IDs
        parsedAnalysis.clauses.forEach((clause, index) => {
          setTimeout(() => {
            handleAddClause(selectedContractId, {
              clauseTitle: clause.clauseTitle || `Clause ${index + 1}`,
              clauseContent: clause.clauseContent || '',
              category: clause.category || 'Other',
              priority: clause.priority || 'medium',
              status: clause.status || 'pending',
              comments: clause.comments || '',
              reviewedBy: clause.reviewedBy || 'AI Analysis',
              riskAssessment: clause.riskAssessment,
              negotiationAnalysis: clause.negotiationAnalysis,
              recommendations: clause.recommendations,
              classification: clause.classification,
              sectionReference: clause.sectionReference,
              // Add all the detailed fields
              fullClauseText: clause.fullClauseText,
              obligations: clause.obligations,
              keyTerms: clause.keyTerms,
              crossReferences: clause.crossReferences,
              detailedRiskAnalysis: clause.detailedRiskAnalysis,
              negotiationStrategy: clause.negotiationStrategy,
              actionableRecommendations: clause.actionableRecommendations,
              benchmarkComparison: clause.benchmarkComparison
            } as any);
          }, index * 10); // 10ms delay between each clause
        });
      }

      // Store the analysis document
      const analysisDoc: AnalysisDocument = {
        fileName: file.name,
        fileUrl: URL.createObjectURL(file),
        uploadDate: new Date(),
        fileSize: file.size,
        uploadedBy: currentUser
      };

      setContracts(prevContracts => 
        prevContracts.map(c => 
          c.id === selectedContractId 
            ? { ...c, analysisDocument: analysisDoc }
            : c
        )
      );

      alert('✅ Contract analysis completed successfully with enhanced risk assessment and negotiation insights!');
    } catch (error) {
      console.error('Error processing file:', error);
      let errorMessage = 'Error processing file: ';
      
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      alert(`❌ ${errorMessage}`);
    }
  };

  const handleSummaryUpdate = (contractId: string, summary: Partial<ContractSummary>) => {
    setContracts(contracts.map(c => 
      c.id === contractId 
        ? { ...c, contractSummary: { ...c.contractSummary, ...summary } as ContractSummary }
        : c
    ));
  };

  const handleAddClause = (contractId: string, clause: Omit<ReviewClause, 'clauseId' | 'reviewDate' | 'negotiationHistory'>) => {
    const newClause: ReviewClause = {
      ...clause,
      clauseId: `contract_${contractId}_clause_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      reviewDate: new Date(),
      negotiationHistory: []
    };
    setReviewClauses(prevClauses => {
      const updatedClauses = [...prevClauses, newClause];
      return updatedClauses;
    });
  };

  const handleUpdateClause = (clauseId: string, updates: Partial<ReviewClause>) => {
    setReviewClauses(reviewClauses.map(c => 
      c.clauseId === clauseId ? { ...c, ...updates } : c
    ));
  };

  const handleDeleteClause = (clauseId: string) => {
    setReviewClauses(reviewClauses.filter(c => c.clauseId !== clauseId));
  };

  const handleAddNegotiationPoint = (point: Omit<NegotiationPointType, 'id' | 'createdDate' | 'lastModified'>) => {
    const newPoint: NegotiationPointType = {
      ...point,
      id: `np_${Date.now()}`,
      createdDate: new Date(),
      lastModified: new Date()
    };
    setNegotiationPoints([...negotiationPoints, newPoint]);
  };

  const handleUpdateNegotiationPoint = (id: string, updates: Partial<NegotiationPointType>) => {
    setNegotiationPoints(negotiationPoints.map(p => 
      p.id === id ? { ...p, ...updates, lastModified: new Date() } : p
    ));
  };

  const handleDeleteNegotiationPoint = (id: string) => {
    setNegotiationPoints(negotiationPoints.filter(p => p.id !== id));
  };

  // Load data from backend
  const loadData = useCallback(async () => {
    if (!token) return;
    
    try {
      // Load suppliers from backend
      const suppliersData = await supplierService.getAll(token);
      // Transform FrontendSupplier to Supplier format
      const transformedSuppliers: Supplier[] = (suppliersData || []).map((supplier: any) => ({
        id: supplier.id,
        companyName: supplier.companyName || supplier.name,
        taxId: supplier.taxId || '',
        registrationNumber: supplier.registrationNumber,
        address: supplier.address ? (() => {
          // Handle both string and object address formats
          if (typeof supplier.address === 'string') {
            // Parse string format: "street, city, state, zipCode, country"
            const parts = (supplier.address as string).split(',').map((p: string) => p.trim());
            return {
              street: parts[0] || '',
              city: parts[1] || '',
              state: parts[2] || '',
              zipCode: parts[3] || '',
              country: parts[4] || 'United States'
            };
          } else {
            // Handle object format
            return {
              street: supplier.address.street || '',
              city: supplier.address.city || '',
              state: supplier.address.state || '',
              zipCode: supplier.address.zipCode || '',
              country: supplier.address.country || 'United States'
            };
          }
        })() : {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'United States'
        },
        contactPersons: supplier.contactPersons || [{
          name: supplier.contactPerson || '',
          role: 'Primary Contact',
          email: supplier.email || '',
          phone: supplier.phone || '',
          isPrimary: true
        }],
        category: supplier.category || 'Other',
        status: supplier.status || 'active',
        performanceRating: supplier.performanceRating,
        notes: supplier.notes,
        registrationDate: supplier.registrationDate,
        website: supplier.website,
        // Legacy fields for backward compatibility
        name: supplier.companyName || supplier.name,
        contactPerson: supplier.contactPersons?.[0]?.name || supplier.contactPerson || '',
        email: supplier.contactPersons?.[0]?.email || supplier.email || '',
        phone: supplier.contactPersons?.[0]?.phone || supplier.phone || ''
      }));
      setSuppliers(transformedSuppliers);

      // Load contract groups from backend
      const groupsData = await contractGroupService.getActive(token);
      setContractGroups(groupsData);

      // Load contracts from backend
      const contractsData = await contractService.getAll(token);
      // Transform backend contracts to frontend format (or empty)
      const transformedContracts: Contract[] = (contractsData || []).map((contract: ContractApiResponse) => {
        // Find supplier name - first try from populated data, then fallback to suppliers array
        let supplierName = '';
        if (contract.supplier?.companyName) {
          supplierName = contract.supplier.companyName;
        } else if (contract.supplier?.name) {
          supplierName = contract.supplier.name;
        } else if (contract.supplierId) {
          // Fallback: find supplier in loaded suppliers array
          const supplier = transformedSuppliers.find(s => s.id?.toString() === contract.supplierId?.toString());
          supplierName = supplier?.companyName || supplier?.name || '';
        }

        // Use the shared generateNumericId function

        const rawId = contract._id || contract.id?.toString() || '';
        const numericId = generateNumericId(rawId);

        // Find group name from loaded groups
        const contractGroupId = (contract as any).groupId?.toString?.() || (contract as any).groupId;
        const group = groupsData.find((g: ContractGroup) => (g.id || g._id) === contractGroupId);
        const groupName = group?.name || '';

        return {
          id: rawId, // Use string ID for frontend
          _id: contract._id || contract.id?.toString(), // Preserve original ObjectId for backend operations
          supplierId: (contract.supplierId as any)?.toString?.() || contract.supplierId,
          supplierName,
          contractTitle: contract.contractTitle,
          year: contract.year,
          startDate: contract.startDate,
          endDate: contract.endDate,
          status: contract.status,
          reviewStatus: contract.reviewStatus,
          description: contract.description,
          paymentTerms: contract.paymentTerms,
          renewalDate: contract.renewalDate,
          attachments: contract.attachments || [],
          negotiationPoints: contract.negotiationPoints || [],
          // Include the new fields from API response
          minPurchaseAmount: contract.minPurchaseAmount,
          marketingBudget: contract.marketingBudget,
          renewalTermsCount: contract.renewalTermsCount,
          incotermTerms: contract.incotermTerms,
          paymentCurrency: contract.paymentCurrency,
          groupId: contractGroupId,
          groupName,
          category: (contract as any).category ?? null,
          // Include negotiation email if available
          negotiationEmail: (contract as any).negotiationEmail
        };
      });
      setContracts(transformedContracts);
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load data from server.');
      // Do not populate any mock data
      setSuppliers([]);
      setContracts([]);
    }
  }, [token]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);



  // Removed unused function - handleAddNegotiationPointOld
  /* const _handleAddNegotiationPointOld = (contractId: number | null) => {
    if (!newNegotiationPoint.category || !newNegotiationPoint.point) return;
    
    const negotiationPoint: NegotiationPointType = {
      id: `np_${Date.now()}`,
      contractId: contractId || 0,
      title: newNegotiationPoint.point.substring(0, 50),
      description: newNegotiationPoint.point,
      category: newNegotiationPoint.category as any,
      priority: newNegotiationPoint.priority,
      status: newNegotiationPoint.status,
      createdDate: new Date(),
      createdBy: currentUser,
      lastModified: new Date()
    };

    if (contractId) {
      handleAddNegotiationPoint(negotiationPoint);
    }
    
    setNewNegotiationPoint({
      category: '',
      point: '',
      priority: 'medium',
      status: 'pending'
    });
    }; */
  
  const handleSaveContract = async () => {
    const supplier = suppliers.find(s => s.id?.toString() === (contractFormData.supplierId as string).toString());

    try {
      // Validate supplier id format
      const supplierIdString = String(contractFormData.supplierId || '').trim();
      if (!supplierIdString || !isValidObjectId(supplierIdString)) {
        showError('Please select a valid supplier (requires a real backend supplier).');
        return;
      }
      if (editingContract) {
        // Update existing contract via API
        if (token) {
          // Use the original ObjectId if available, otherwise fall back to the numeric ID
          let contractId = editingContract._id || editingContract.id.toString();
          
          // If we still don't have a valid ObjectId, this contract doesn't exist in the backend
          if (!isValidObjectId(contractId)) {
            console.warn(`Contract ID ${contractId} is not a valid ObjectId. This contract may not exist in the database.`);
            showError('Cannot update contract: This contract may not exist in the database. Please create it first.');
            return;
          }
          
          await contractService.update(
            token,
            contractId,
            { ...contractFormData, supplierId: supplierIdString }
          );
          
          // Update local state
          const group = contractGroups.find(g => (g.id || g._id) === contractFormData.groupId);
          setContracts(contracts.map(c =>
            c.id === editingContract.id
              ? {
                  ...c,
                  ...contractFormData,
                  supplierName: supplier?.name || '',
                  supplierId: supplierIdString,
                  groupName: group?.name || ''
                }
              : c
          ));
          showSuccess('Contract updated successfully!');
        } else {
          // Fallback to local update if no token
          setContracts(contracts.map(c => 
            c.id === editingContract.id 
              ? { ...c, ...contractFormData, supplierName: supplier?.name || '', supplierId: parseInt(contractFormData.supplierId as string) }
              : c
          ));
        }
      } else {
        // Create new contract via API
        if (token) {
          const createdContract = await contractService.create(token, { ...contractFormData, supplierId: supplierIdString });

          // Add to local state with the ID from backend
          // Use consistent ID generation method
          const rawId = createdContract._id || createdContract.id?.toString() || Date.now().toString();
          const newGroup = contractGroups.find(g => (g.id || g._id) === contractFormData.groupId);
          const newContract: Contract = {
            ...contractFormData,
            id: rawId, // Use string ID
            _id: rawId, // Preserve ObjectId
            supplierName: supplier?.name || '',
            supplierId: supplierIdString,
            negotiationPoints: [],
            groupName: newGroup?.name || ''
          };
          setContracts([...contracts, newContract]);
          showSuccess('Contract created successfully!');
        } else {
          // Fallback to local creation if no token
          const newContract: Contract = {
            ...contractFormData,
            id: Date.now().toString(),
            supplierName: supplier?.name || '',
            supplierId: supplierIdString,
            negotiationPoints: []
          };
          setContracts([...contracts, newContract]);
        }
      }
      
      setShowContractModal(false);
      setEditingContract(null);
      // Reset form
      setContractFormData({
        supplierId: '',
        contractTitle: '',
        year: new Date().getFullYear(),
        startDate: '',
        endDate: '',
        status: 'draft',
        reviewStatus: 'not_received',
        description: '',
        paymentTerms: '',
        renewalDate: '',
        attachments: [],
        negotiationPoints: [],
        // New fields
        minPurchaseAmount: undefined,
        marketingBudget: undefined,
        renewalTermsCount: undefined,
        incotermTerms: '',
        paymentCurrency: '',
        groupId: undefined,
        category: null
      });
    } catch (error) {
      console.error('Error saving contract:', error);
      showError('Failed to save contract. Please try again.');
    }
  };

  const handleSaveSupplier = async () => {
    try {
      // Prepare data for backend API
      const supplierData = {
        companyName: supplierForm.companyName,
        taxId: supplierForm.taxId,
        registrationNumber: supplierForm.registrationNumber || undefined,
        address: {
          street: supplierForm.address.street,
          city: supplierForm.address.city,
          state: supplierForm.address.state,
          zipCode: supplierForm.address.zipCode,
          country: supplierForm.address.country
        },
        contactPersons: supplierForm.contactPersons.map(contact => ({
          name: contact.name,
          role: contact.role,
          email: contact.email,
          phone: contact.phone,
          isPrimary: contact.isPrimary
        })),
        category: supplierForm.category,
        status: supplierForm.status,
        createdBy: user?.id // Add current user ID
      };

      if (editingSupplier) {
        // Update existing supplier via API
        if (token) {
          await supplierService.update(
            token,
            editingSupplier.id.toString(),
            supplierData as any
          );
          
          // Update local state
          const updatedSupplier = {
            ...supplierForm,
            id: editingSupplier.id,
            // Add legacy fields for backward compatibility
            name: supplierForm.companyName,
            contactPerson: supplierForm.contactPersons[0]?.name || '',
            email: supplierForm.contactPersons[0]?.email || '',
            phone: supplierForm.contactPersons[0]?.phone || ''
          };
          setSuppliers(suppliers.map(s => 
            s.id === editingSupplier.id ? updatedSupplier : s
          ));
          showSuccess('Supplier updated successfully!');
        }
      } else {
        // Create new supplier via API
        if (token) {
          const createdSupplier = await supplierService.create(token, supplierData as any);
          
          // Add to local state with the ID from backend
          const newSupplier: Supplier = { 
            ...supplierForm, 
            id: createdSupplier.id || Date.now(),
            // Add legacy fields for backward compatibility
            name: supplierForm.companyName,
            contactPerson: supplierForm.contactPersons[0]?.name || '',
            email: supplierForm.contactPersons[0]?.email || '',
            phone: supplierForm.contactPersons[0]?.phone || ''
          };
          setSuppliers([...suppliers, newSupplier]);
          showSuccess('Supplier created successfully!');
        }
      }
      
      setShowSupplierModal(false);
      setEditingSupplier(null);
      // Reset form to initial state
      setSupplierForm({
        id: 0,
        companyName: '',
        taxId: '',
        registrationNumber: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'United States'
        },
        contactPersons: [{
          name: '',
          role: '',
          email: '',
          phone: '',
          isPrimary: true
        }],
        category: 'Technology',
        status: 'active'
      });
    } catch (error) {
      console.error('Error saving supplier:', error);
      showError('Failed to save supplier. Please try again.');
    }
  };

  const handleDeleteContract = async (contractId: string) => {
    if (!token) {
      showError('Authentication required');
      return;
    }
    
    try {
      // Find the contract to get the proper ObjectId
      const contract = contracts.find(c => c.id === contractId);
      const actualContractId = contract?._id;
      
      if (!actualContractId || !isValidObjectId(actualContractId)) {
        showError('Cannot delete contract: Invalid contract ID. This contract may not exist in the database.');
        return;
      }
      
      await contractService.delete(token, actualContractId);
      
      // Update local state
      setContracts(contracts.filter(c => c.id !== contractId));
      showSuccess('Contract deleted successfully');
    } catch (error) {
      console.error('Contract deletion failed:', error);
      showError('Failed to delete contract');
    }
  };

  const handleDeleteSupplier = async (id: number) => {
    try {
      if (token) {
        // Delete via API
        await supplierService.delete(token, id.toString());
        showSuccess('Supplier deleted successfully!');
      }
      // Update local state regardless
      setSuppliers(suppliers.filter(s => s.id !== id));
      setContracts(contracts.filter(c => c.supplierId !== id));
    } catch (error) {
      console.error('Error deleting supplier:', error);
      showError('Failed to delete supplier. Please try again.');
    }
  };

  // Handle contract selection
  const handleSelectContract = (contractId: string) => {
    const newSelected = new Set(selectedContracts);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedContracts(newSelected);
  };

  // Handle select all contracts
  const handleSelectAll = () => {
    if (selectedContracts.size === filteredContracts.length) {
      setSelectedContracts(new Set());
    } else {
      setSelectedContracts(new Set(filteredContracts.map(c => c.id)));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!token) {
      showError('Authentication required');
      return;
    }
    
    try {
      // Convert numeric IDs to ObjectIds
      const contractObjectIds: string[] = [];
      const selectedIds = Array.from(selectedContracts);
      
      for (const numericId of selectedIds) {
        const contract = contracts.find(c => c.id === numericId);
        if (contract?._id && isValidObjectId(contract._id)) {
          contractObjectIds.push(contract._id);
        }
      }
      
      if (contractObjectIds.length === 0) {
        showError('No valid contracts selected for deletion');
        return;
      }
      
      if (contractObjectIds.length !== selectedIds.length) {
        console.warn(`Warning: Only ${contractObjectIds.length} out of ${selectedIds.length} selected contracts have valid ObjectIds`);
      }
      
      await contractService.bulkDelete(token, contractObjectIds);
      
      // Update local state - remove deleted contracts
      setContracts(contracts.filter(c => !selectedContracts.has(c.id)));
      setSelectedContracts(new Set());
      setShowBulkDeleteConfirm(false);
      
      showSuccess(`${contractObjectIds.length} contract${contractObjectIds.length === 1 ? '' : 's'} deleted successfully!`);
    } catch (error) {
      console.error('Error deleting contracts:', error);
      showError('Failed to delete contracts. Please try again.');
    }
  };

  const openContractDetails = (contract: Contract) => {
    // Navigate to the new 5-step contract workflow
    const contractId = contract._id || contract.id;
    navigate(`/contracts/${contractId}`);
  };

  // Open the contract details modal (for viewing saved negotiation email and details)
  const openContractModal = (contract: Contract, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedContract(contract);
    setIsModalOpen(true);
  };

  const closeContractDetails = () => {
    setSelectedContract(null);
    setIsModalOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-600 text-white dark:bg-green-500 dark:text-white';
      case 'draft': return 'bg-amber-500 text-white dark:bg-amber-400 dark:text-gray-900';
      case 'expired': return 'bg-red-600 text-white dark:bg-red-500 dark:text-white';
      case 'terminated': return 'bg-gray-600 text-white dark:bg-gray-500 dark:text-white';
      default: return 'bg-gray-600 text-white dark:bg-gray-500 dark:text-white';
    }
  };

  const getReviewStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'finalized': return 'bg-green-600 text-white dark:bg-green-500 dark:text-white';
      case 'under_review': return 'bg-blue-600 text-white dark:bg-blue-500 dark:text-white';
      case 'pending_review': return 'bg-amber-500 text-white dark:bg-amber-400 dark:text-gray-900';
      case 'not_received': return 'bg-gray-500 text-white dark:bg-gray-400 dark:text-gray-900';
      default: return 'bg-gray-600 text-white dark:bg-gray-500 dark:text-white';
    }
  };

  // Helper function to format review status for display
  const formatReviewStatus = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'not_received': return 'Not Received';
      case 'pending_review': return 'Pending Review';
      case 'under_review': return 'Under Review';
      case 'finalized': return 'Finalized';
      default: return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    }
  };

  // Handle column sorting
  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Calculate tenure between start and end dates
  // Removed unused function - calculateTenure
  /* const _calculateTenure = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const months = Math.floor(diffDays / 30);
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years > 0) {
      return remainingMonths > 0 ? `${years}y ${remainingMonths}m` : `${years} year${years > 1 ? 's' : ''}`;
    }
    return `${months} month${months !== 1 ? 's' : ''}`;
  }; */

  // Get dynamic status based on contract dates
  const getDynamicStatus = (contract: Contract): { status: string; color: string; expiryWarning?: string } => {
    const today = new Date();
    const endDate = new Date(contract.endDate);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        status: 'Expired',
        color: 'bg-red-600 text-white dark:bg-red-500 dark:text-white',
        expiryWarning: 'expired'
      };
    } else if (diffDays <= 30) {
      return {
        status: contract.status,
        color: getStatusColor(contract.status),
        expiryWarning: `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`
      };
    } else if (diffDays <= 90) {
      return {
        status: contract.status,
        color: getStatusColor(contract.status),
        expiryWarning: `Expires in ${diffDays} days`
      };
    }

    // Return original status with appropriate color - contract is active
    return { status: contract.status, color: getStatusColor(contract.status), expiryWarning: undefined };
  };

  // Filter contracts - memoized to prevent re-computation on every render
  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const matchesSearch = contract.contractTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           contract.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
      const matchesSupplier = selectedSupplier === 'all' || contract.supplierId?.toString() === selectedSupplier;
      const matchesReviewStatus = reviewStatusFilter === 'all' || contract.reviewStatus === reviewStatusFilter;
      const matchesGroup = groupFilter === 'all' || contract.groupId === groupFilter;

      let matchesEndDate = true;
      if (endDateFilter !== 'all') {
        const endDate = new Date(contract.endDate);
        const today = new Date();
        const daysDiff = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        switch (endDateFilter) {
          case '30days':
            matchesEndDate = daysDiff <= 30 && daysDiff > 0;
            break;
          case '60days':
            matchesEndDate = daysDiff <= 60 && daysDiff > 0;
            break;
          case '90days':
            matchesEndDate = daysDiff <= 90 && daysDiff > 0;
            break;
          case 'expired':
            matchesEndDate = daysDiff < 0;
            break;
        }
      }

      let matchesDateRange = true;
      if (startDateFrom || startDateTo) {
        const contractStart = new Date(contract.startDate);
        if (startDateFrom && contractStart < new Date(startDateFrom)) {
          matchesDateRange = false;
        }
        if (startDateTo && contractStart > new Date(startDateTo)) {
          matchesDateRange = false;
        }
      }

      return matchesSearch && matchesStatus && matchesSupplier && matchesReviewStatus && matchesGroup && matchesEndDate && matchesDateRange;
    }).sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      switch (sortField) {
        case 'contractTitle':
          aValue = a.contractTitle.toLowerCase();
          bValue = b.contractTitle.toLowerCase();
          break;
        case 'supplierName':
          aValue = a.supplierName.toLowerCase();
          bValue = b.supplierName.toLowerCase();
          break;
        case 'groupName':
          aValue = (a.groupName || '').toLowerCase();
          bValue = (b.groupName || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        case 'reviewStatus':
          aValue = a.reviewStatus.toLowerCase();
          bValue = b.reviewStatus.toLowerCase();
          break;
        case 'endDate':
          aValue = new Date(a.endDate);
          bValue = new Date(b.endDate);
          break;
        default:
          aValue = a.contractTitle.toLowerCase();
          bValue = b.contractTitle.toLowerCase();
      }

      if (sortField === 'endDate') {
        const comparison = (aValue as Date).getTime() - (bValue as Date).getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      const comparison = (aValue as string).localeCompare(bValue as string);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [contracts, searchTerm, statusFilter, selectedSupplier, reviewStatusFilter, groupFilter, endDateFilter, startDateFrom, startDateTo, sortField, sortDirection]);

  // Calculate statistics - memoized to prevent re-computation on every render
  const stats = useMemo(() => {
    const total = contracts.length;
    const active = contracts.filter(c => c.status === 'active').length;
    const expiring = contracts.filter(c => {
      const endDate = new Date(c.endDate);
      const today = new Date();
      const daysDiff = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff <= 30 && daysDiff > 0;
    }).length;
    const draft = contracts.filter(c => c.status === 'draft').length;

    const notReceived = contracts.filter(c => c.reviewStatus === 'not_received').length;
    const pendingReview = contracts.filter(c => c.reviewStatus === 'pending_review').length;
    const underReview = contracts.filter(c => c.reviewStatus === 'under_review').length;
    const finalized = contracts.filter(c => c.reviewStatus === 'finalized').length;

    const totalNegotiationPoints = contracts.reduce((sum, contract) => sum + contract.negotiationPoints.length, 0);
    const pendingNegotiations = contracts.reduce((sum, contract) =>
      sum + contract.negotiationPoints.filter(np => np.status === 'pending').length, 0
    );

    return {
      total, active, expiring, draft,
      notReceived, pendingReview, underReview, finalized,
      totalNegotiationPoints, pendingNegotiations
    };
  }, [contracts]);

  const handleUpdateContract = (contractId: string, updates: Partial<Contract>) => {
    setContracts(contracts.map(c =>
      c.id === contractId ? { ...c, ...updates } : c
    ));
  };

  // Handler for inline status updates
  const handleStatusUpdate = async (contractId: string, field: string, value: string) => {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;

    // Get the actual MongoDB _id for the API call
    const actualContractId = contract._id || contractId;

    // Update locally first for immediate feedback
    handleUpdateContract(contractId, { [field]: value } as Partial<Contract>);

    // Persist to backend if token is available
    if (token) {
      try {
        await contractService.update(token, actualContractId, { [field]: value });
      } catch (error) {
        console.error('Failed to update contract status:', error);
        // Revert on error
        handleUpdateContract(contractId, { [field]: contract[field as keyof Contract] } as Partial<Contract>);
        throw error;
      }
    }
  };

  // Wrapper component for ContractReviewPage to extract route params
  const ContractReviewPageWrapper = () => {
    const { id } = useParams<{ id: string }>();
    const contractId = id || '0';
    const contract = contracts.find(c => c.id === contractId);
    
    // Map local Contract to ContractType
    const mappedContract = contract ? {
      ...contract,
      contractTitle: contract.contractTitle,
      supplier: contract.supplierName,
      value: 0, // Default value since it's not in the local Contract type
      category: 'General' // Default category
    } as ContractType : undefined;
    
    return (
      <ContractReviewPage
        contractId={contractId}
        contract={mappedContract}
        onUpdateContract={handleUpdateContract}
      />
    );
  };

  const [selectedContracts, setSelectedContracts] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Removed autosave functionality as requested

  return (
    <Routes>
      <Route path="/contracts/:id/review" element={
        <ContractReviewPageWrapper />
      } />
      <Route path="*" element={
        <div className="flex h-screen bg-beige-100 dark:bg-dark-bg transition-colors duration-300">
          {/* Elegant Sidebar Navigation */}
          <aside className={`${sidebarCollapsed ? 'w-20' : 'w-72'} bg-white dark:bg-dark-surface border-r border-gray-200 dark:border-dark-border shadow-lg transition-all duration-300 flex flex-col relative`}>
            {/* Brand Header */}
            <div className={`${sidebarCollapsed ? 'p-4' : 'p-6'} border-b border-gray-100 dark:border-dark-border`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-600 via-primary-500 to-primary-400 dark:from-accent-500 dark:via-accent-400 dark:to-accent-300 rounded-xl shadow-lg shadow-primary/20 dark:shadow-accent/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                {!sidebarCollapsed && (
                  <div>
                    <h1 className="text-2xl font-display font-light text-gray-800 dark:text-white tracking-tight">BIB ContractHub</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Contract Management</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar Toggle Button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 dark:hover:bg-dark-surface-raised transition-colors z-10"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
            </button>
            
            {/* Elegant Navigation */}
            <nav className={`${sidebarCollapsed ? 'p-2' : 'p-4'} space-y-2 flex-1`}>
              <PermissionGuard permission="reports.view">
                <button
                  onClick={() => setActiveTab('overview')}
                  title={sidebarCollapsed ? 'Overview - Dashboard & Analytics' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl ${
                    activeTab === 'overview'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary/25 dark:shadow-accent/25'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-xl transition-all duration-200 ${
                    activeTab === 'overview'
                      ? 'bg-white/20'
                      : 'bg-primary-50 dark:bg-primary-900/30 text-primary-500 dark:text-primary-400 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50'
                  }`}>
                    <BarChart className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">Overview</span>
                      <p className={`text-xs ${activeTab === 'overview' ? 'text-primary-100 dark:text-gray-600' : 'text-gray-500 dark:text-gray-500'}`}>
                        Dashboard & Analytics
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>

              <PermissionGuard permission="contracts.view">
                <button
                  onClick={() => setActiveTab('contracts')}
                  title={sidebarCollapsed ? 'Contracts - Manage & Upload' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl ${
                    activeTab === 'contracts'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary/25 dark:shadow-accent/25'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-xl transition-all duration-200 ${
                    activeTab === 'contracts'
                      ? 'bg-white/20'
                      : 'bg-accent-50 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400 group-hover:bg-accent-100 dark:group-hover:bg-accent-900/50'
                  }`}>
                    <FileText className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">Contracts</span>
                      <p className={`text-xs ${activeTab === 'contracts' ? 'text-primary-100 dark:text-gray-600' : 'text-gray-500 dark:text-gray-500'}`}>
                        Manage & Upload
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>

              <PermissionGuard permission="contracts.files.view">
                <button
                  onClick={() => setActiveTab('repository')}
                  title={sidebarCollapsed ? 'Repository - All Contract Documents' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl ${
                    activeTab === 'repository'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary/25 dark:shadow-accent/25'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-xl transition-all duration-200 ${
                    activeTab === 'repository'
                      ? 'bg-white/20'
                      : 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 group-hover:bg-amber-100 dark:group-hover:bg-amber-900/50'
                  }`}>
                    <Archive className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">Repository</span>
                      <p className={`text-xs ${activeTab === 'repository' ? 'text-primary-100 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                        All Documents
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>

              <PermissionGuard permissions={["contracts.view", "contracts.approve"]} requireAll={false}>
                <button
                  onClick={() => setActiveTab('contract-review')}
                  title={sidebarCollapsed ? 'MyLegalAI - AI Contract Analysis' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl ${
                    activeTab === 'contract-review'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary/25 dark:shadow-accent/25'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-xl transition-all duration-200 ${
                    activeTab === 'contract-review'
                      ? 'bg-white/20'
                      : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50'
                  }`}>
                    <FileCheck className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">MyLegalAI</span>
                      <p className={`text-xs ${activeTab === 'contract-review' ? 'text-primary-100 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                        AI Contract Analysis
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>

              {/* Document Generator - Navigate to separate workspace */}
              <PermissionGuard permissions={["contracts.view", "contracts.create"]} requireAll={false}>
                <button
                  onClick={() => navigate('/document-generator')}
                  title={sidebarCollapsed ? 'Document Generator - Create Internal Documents' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm`}
                >
                  <div className="p-2 rounded-xl transition-all duration-200 bg-teal-50 dark:bg-teal-900/30 text-teal-500 dark:text-teal-400 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/50">
                    <FilePlus2 className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">Doc Generator</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Create Documents
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>

              <PermissionGuard permission="rules.view">
                <button
                  onClick={() => setActiveTab('rule-book')}
                  title={sidebarCollapsed ? 'Rule Book - Finance rules' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl ${
                    activeTab === 'rule-book'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary/25 dark:shadow-accent/25'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-xl transition-all duration-200 ${
                    activeTab === 'rule-book'
                      ? 'bg-white/20'
                      : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50'
                  }`}>
                    <BookOpenCheck className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">Rule Book</span>
                      <p className={`text-xs ${activeTab === 'rule-book' ? 'text-primary-100 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                        Finance Rules
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>

              <PermissionGuard permission="masterdata.view">
                <button
                  onClick={() => setActiveTab('master-data')}
                  title={sidebarCollapsed ? 'Master Data - Settings & Users' : undefined}
                  className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-4 ${sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'} text-left transition-all duration-200 rounded-2xl ${
                    activeTab === 'master-data'
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary/25 dark:shadow-accent/25'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-beige-100/80 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white hover:shadow-sm'
                  }`}
                >
                  <div className={`p-2 rounded-xl transition-all duration-200 ${
                    activeTab === 'master-data'
                      ? 'bg-white/20'
                      : 'bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/50'
                  }`}>
                    <Database className="w-5 h-5" />
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <span className="font-medium">Master Data</span>
                      <p className={`text-xs ${activeTab === 'master-data' ? 'text-primary-100 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                        Settings & Users
                      </p>
                    </div>
                  )}
                </button>
              </PermissionGuard>
            </nav>
            
            {/* Elegant Footer */}
            <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-t border-gray-100 dark:border-dark-border mt-auto`}>
              <div className={`bg-beige-50 dark:bg-dark-surface-raised rounded-xl ${sidebarCollapsed ? 'p-2' : 'p-4'} border border-gray-100 dark:border-dark-border`}>
                <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-accent-400 dark:to-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-medium text-xs">v1</span>
                  </div>
                  {!sidebarCollapsed && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">System Status</p>
                      <p className="text-xs text-accent-600 dark:text-accent-400">All systems operational</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden bg-beige-50 dark:bg-dark-bg-secondary">
            {/* Elegant Header */}
            <header className="bg-white dark:bg-dark-surface shadow-sm border-b border-gray-100 dark:border-dark-border">
              <div className="px-8 py-6">
                <div className="flex items-center justify-between">
                  {/* Left Section - Current Tab Title */}
                  <div className="flex items-center gap-4">
                    <h2 className="text-2xl font-light text-gray-800 dark:text-white tracking-tight capitalize">
                      {activeTab === 'contract-review' ? 'MyLegalAI' :
                       activeTab === 'master-data' ? 'Master Data' :
                       activeTab === 'repository' ? 'Repository' :
                       activeTab === 'rule-book' ? 'Rule Book' :
                       activeTab}
                    </h2>
                    <div className="px-3 py-1 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-accent-400 rounded-full text-sm font-medium">
                      {activeTab === 'overview' ? 'Dashboard' :
                       activeTab === 'contracts' ? 'Management' :
                       activeTab === 'contract-review' ? 'AI Assistant' :
                       activeTab === 'repository' ? 'Documents' :
                       activeTab === 'master-data' ? 'Settings' :
                       activeTab === 'rule-book' ? 'Finance' : 'Active'}
                    </div>
                  </div>
                  
                  {/* Right Section - Actions and Status */}
                  <div className="flex items-center gap-4">
                    <PermissionGuard permission="contracts.create">
                      <button
                        onClick={() => {
                          // Reset form data for new contract
                          setContractFormData({
                            supplierId: '',
                            contractTitle: '',
                            year: new Date().getFullYear(),
                            startDate: '',
                            endDate: '',
                            status: 'draft',
                            reviewStatus: 'not_received',
                            description: '',
                            paymentTerms: '',
                            renewalDate: '',
                            attachments: [],
                            negotiationPoints: [],
                            // New fields - reset to empty/undefined
                            minPurchaseAmount: undefined,
                            marketingBudget: undefined,
                            renewalTermsCount: undefined,
                            incotermTerms: '',
                            paymentCurrency: '',
                            category: null
                          });
                          setEditingContract(null);
                          setShowContractModal(true);
                        }}
                        className="group flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white rounded-xl hover:shadow-lg hover:shadow-primary/25 dark:shadow-accent/25 transition-all duration-200"
                      >
                        <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
                        <span className="font-medium">Add Contract</span>
                      </button>
                    </PermissionGuard>

                    <UserDropdown />
                  </div>
                </div>
              </div>
            </header>

            {/* Content Area */}
            <main className="flex-1 overflow-x-hidden overflow-y-auto bg-beige-50 dark:bg-dark-bg-secondary p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard Overview</h2>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm hover:shadow-md transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Contracts</p>
                          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.total}</p>
                        </div>
                        <div className="p-3 bg-primary-50 dark:bg-primary-900/30 rounded-xl">
                          <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-accent-100 dark:border-accent-900/30 shadow-sm hover:shadow-md transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Contracts</p>
                          <p className="text-3xl font-bold text-accent-600 dark:text-accent-400 mt-2">{stats.active}</p>
                        </div>
                        <div className="p-3 bg-accent-50 dark:bg-accent-900/30 rounded-xl">
                          <CheckCircle className="w-6 h-6 text-accent-600 dark:text-accent-400" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-warning-100 dark:border-warning-900/30 shadow-sm hover:shadow-md transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiring Soon</p>
                          <p className="text-3xl font-bold text-warning-600 dark:text-warning-400 mt-2">{stats.expiring}</p>
                        </div>
                        <div className="p-3 bg-warning-100 dark:bg-warning-900/50 rounded-xl">
                          <AlertCircle className="w-6 h-6 text-warning-600 dark:text-warning-400" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm hover:shadow-md transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Draft Contracts</p>
                          <p className="text-3xl font-bold text-gray-600 dark:text-gray-300 mt-2">{stats.draft}</p>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                          <Edit2 className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts and Recent Activity */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Review Status Chart */}
                    <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Review Status Distribution</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Finalized</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{stats.finalized}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Under Review</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{stats.underReview}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-warning-500 rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Pending Review</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{stats.pendingReview}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-error-500 rounded-full"></div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">Not Received</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{stats.notReceived}</span>
                        </div>
                      </div>
                    </div>

                    {/* Negotiation Points Summary */}
                    <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Negotiation Points</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-beige-50 dark:bg-dark-bg-secondary rounded-lg border border-gray-100 dark:border-dark-border">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Total Points</span>
                          <span className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalNegotiationPoints}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-warning-50 dark:bg-dark-bg-tertiary rounded-lg border border-warning-200 dark:border-warning-700/50">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Pending</span>
                          <span className="text-2xl font-bold text-warning-600 dark:text-warning-400">{stats.pendingNegotiations}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Contracts - Redesigned */}
                  <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200 dark:border-dark-border">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Contracts</h3>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Last 5 contracts</span>
                      </div>
                    </div>
                    <div className="p-4 space-y-4">
                      {contracts.slice(0, 5).map(contract => {
                        const dynamicStatus = getDynamicStatus(contract);
                        const daysUntilExpiry = Math.ceil((new Date(contract.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                        const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                        const isExpired = daysUntilExpiry < 0;
                        
                        return (
                          <div
                            key={contract.id}
                            className="bg-beige-50 dark:bg-dark-bg-secondary rounded-lg p-4 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200 cursor-pointer border border-gray-200 dark:border-dark-border hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md group"
                            onClick={() => openContractDetails(contract)}
                          >
                            {/* Header: Contract Title & Primary Status */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                  {decodeHtmlEntities(contract.contractTitle)}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                    {contract.supplierName || 'Not specified'}
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">{contract.year}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <StatusDropdown
                                  contractId={contract.id}
                                  currentStatus={contract.status}
                                  type="status"
                                  onUpdate={handleStatusUpdate}
                                />
                                <StatusDropdown
                                  contractId={contract.id}
                                  currentStatus={contract.reviewStatus}
                                  type="reviewStatus"
                                  onUpdate={handleStatusUpdate}
                                />
                              </div>
                            </div>

                            {/* Content: Dates & Additional Info */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  <span>
                                    <span className="font-medium">Start:</span> {new Date(contract.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  <span>
                                    <span className="font-medium">End:</span> {new Date(contract.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                </div>
                              </div>

                              {/* Right side: Warnings & Additional Info */}
                              <div className="flex items-center gap-3">
                                {/* Negotiation Points Count */}
                                {contract.negotiationPoints && contract.negotiationPoints.length > 0 && (
                                  <div className="flex items-center gap-1 text-primary-600 dark:text-accent-400 bg-primary-100 dark:bg-primary-900/50 px-2 py-1 rounded-md">
                                    <FileCheck className="w-3 h-3" />
                                    <span className="text-xs font-medium">{contract.negotiationPoints.length} points</span>
                                  </div>
                                )}

                                {/* Expiry Warning */}
                                {isExpiringSoon && (
                                  <div className="flex items-center gap-1 text-orange-600 bg-orange-100 px-2 py-1 rounded-md">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span className="text-xs font-medium">{daysUntilExpiry} days left</span>
                                  </div>
                                )}
                                
                                {isExpired && (
                                  <div className="flex items-center gap-1 text-red-600 bg-red-100 px-2 py-1 rounded-md">
                                    <AlertCircle className="w-3 h-3" />
                                    <span className="text-xs font-medium">Expired</span>
                                  </div>
                                )}

                                {/* Info Button - Opens Contract Details Modal */}
                                <button
                                  onClick={(e) => openContractModal(contract, e)}
                                  className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                                  title="View Contract Info & Negotiation Email"
                                >
                                  <Info className="w-4 h-4" />
                                </button>

                                {/* View Details Arrow */}
                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 dark:text-primary-400 transition-colors" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {contracts.length === 0 && (
                        <div className="p-8 text-left">
                          <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Contracts Yet</h4>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">Create your first contract to get started.</p>
                          <PermissionGuard permission="contracts.create">
                            <button
                              onClick={() => setShowContractModal(true)}
                              className="px-4 py-2 bg-primary-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-primary-700 dark:hover:bg-accent-400 inline-flex items-center gap-2 transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                              Create Contract
                            </button>
                          </PermissionGuard>
                        </div>
                      )}

                      {contracts.length > 5 && (
                        <div className="p-4 bg-beige-50 dark:bg-dark-bg-secondary text-left border-t border-gray-100 dark:border-dark-border">
                          <button
                            onClick={() => setActiveTab('contracts')}
                            className="text-sm text-primary-600 dark:text-accent-400 hover:text-primary-700 dark:hover:text-accent-300 font-medium transition-colors"
                          >
                            View All {contracts.length} Contracts →
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'contracts' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contract Management</h2>

                  {/* Filters */}
                  <div className="bg-white dark:bg-dark-surface p-6 rounded-xl border border-gray-100 dark:border-dark-border shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('all');
                            setSelectedSupplier('all');
                            setReviewStatusFilter('all');
                            setEndDateFilter('all');
                            setStartDateFrom('');
                            setStartDateTo('');
                          }}
                          className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                        >
                          Reset Filters
                        </button>
                        <button
                          onClick={() => setShowFilters(!showFilters)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                        >
                          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    {showFilters && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                              <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search contracts..."
                                className="w-full pl-9 pr-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            >
                              <option value="all">All Status</option>
                              <option value="active">Active</option>
                              <option value="draft">Draft</option>
                              <option value="expired">Expired</option>
                              <option value="terminated">Terminated</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier</label>
                            <select
                              value={selectedSupplier}
                              onChange={(e) => setSelectedSupplier(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            >
                              <option value="all">All Suppliers</option>
                              {suppliers.map(supplier => (
                                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Review Status</label>
                            <select
                              value={reviewStatusFilter}
                              onChange={(e) => setReviewStatusFilter(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            >
                              <option value="all">All Review Status</option>
                              <option value="not_received">Not Received</option>
                              <option value="pending_review">Pending Review</option>
                              <option value="under_review">Under Review</option>
                              <option value="finalized">Finalized</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Group</label>
                            <select
                              value={groupFilter}
                              onChange={(e) => setGroupFilter(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            >
                              <option value="all">All Groups</option>
                              {contractGroups.map(group => (
                                <option key={group.id || group._id} value={group.id || group._id}>{group.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                            <select
                              value={endDateFilter}
                              onChange={(e) => setEndDateFilter(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            >
                              <option value="all">All Dates</option>
                              <option value="30days">Next 30 Days</option>
                              <option value="60days">Next 60 Days</option>
                              <option value="90days">Next 90 Days</option>
                              <option value="expired">Expired</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date From</label>
                            <input
                              type="date"
                              value={startDateFrom}
                              onChange={(e) => setStartDateFrom(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date To</label>
                            <input
                              type="date"
                              value={startDateTo}
                              onChange={(e) => setStartDateTo(e.target.value)}
                              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white transition-colors"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bulk Actions */}
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                      {selectedContracts.size > 0 && (
                        <>
                          <PermissionGuard permission="contracts.delete">
                            <button
                              onClick={() => setShowBulkDeleteConfirm(true)}
                              className="px-4 py-2 bg-error text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete Selected ({selectedContracts.size})
                            </button>
                          </PermissionGuard>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Showing {filteredContracts.length} of {contracts.length} contracts
                    </p>
                  </div>

                  {/* Contracts Table */}
                  <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-beige-50 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
                            <th className="sticky left-0 bg-beige-50 dark:bg-dark-bg-secondary px-6 py-4 text-left">
                              <input
                                type="checkbox"
                                checked={selectedContracts.size === filteredContracts.length && filteredContracts.length > 0}
                                onChange={handleSelectAll}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded cursor-pointer"
                              />
                            </th>
                            <th
                              onClick={() => handleSort('contractTitle')}
                              className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-beige-100 dark:hover:bg-dark-bg-tertiary transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                Contract Title
                                {sortField === 'contractTitle' ? (
                                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort('supplierName')}
                              className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-beige-100 dark:hover:bg-dark-bg-tertiary transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                Supplier
                                {sortField === 'supplierName' ? (
                                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort('groupName')}
                              className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-beige-100 dark:hover:bg-dark-bg-tertiary transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                Group
                                {sortField === 'groupName' ? (
                                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort('status')}
                              className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-beige-100 dark:hover:bg-dark-bg-tertiary transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                Status
                                {sortField === 'status' ? (
                                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort('reviewStatus')}
                              className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-beige-100 dark:hover:bg-dark-bg-tertiary transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                Review Status
                                {sortField === 'reviewStatus' ? (
                                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort('endDate')}
                              className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-beige-100 dark:hover:bg-dark-bg-tertiary transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                End Date
                                {sortField === 'endDate' ? (
                                  sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>
                                )}
                              </div>
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                          {filteredContracts.map((contract) => {
                            const dynamicStatus = getDynamicStatus(contract);
                            return (
                              <tr
                                key={contract.id}
                                onClick={() => openContractDetails(contract)}
                                className="hover:bg-beige-50 dark:hover:bg-dark-bg-secondary transition-colors duration-150 cursor-pointer"
                              >
                                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedContracts.has(contract.id)}
                                    onChange={() => handleSelectContract(contract.id)}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded cursor-pointer"
                                  />
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-col space-y-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate">
                                      {decodeHtmlEntities(contract.contractTitle)}
                                    </p>
                                    {/* ID hidden per user request - not relevant for business users */}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <p className="text-sm text-gray-900 dark:text-gray-200">
                                    {contract.supplierName}
                                  </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {contract.groupName ? (
                                    <span
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                    >
                                      <span
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: contractGroups.find(g => (g.id || g._id) === contract.groupId)?.color || '#6b7280' }}
                                      />
                                      {contract.groupName}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <StatusDropdown
                                    contractId={contract.id}
                                    currentStatus={contract.status}
                                    type="status"
                                    onUpdate={handleStatusUpdate}
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <StatusDropdown
                                    contractId={contract.id}
                                    currentStatus={contract.reviewStatus}
                                    type="reviewStatus"
                                    onUpdate={handleStatusUpdate}
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex flex-col">
                                    <p className="text-sm text-gray-900 dark:text-gray-200">
                                      {new Date(contract.endDate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </p>
                                    {dynamicStatus.expiryWarning && (
                                      <span className={`inline-flex items-center gap-1 text-xs mt-1 ${
                                        dynamicStatus.expiryWarning === 'expired'
                                          ? 'text-red-600 dark:text-red-400'
                                          : dynamicStatus.expiryWarning.includes('30') || dynamicStatus.expiryWarning.includes('day')
                                            ? 'text-amber-600 dark:text-amber-400'
                                            : 'text-yellow-600 dark:text-yellow-400'
                                      }`}>
                                        <AlertTriangle className="w-3 h-3" />
                                        {dynamicStatus.expiryWarning === 'expired' ? 'Contract Expired' : dynamicStatus.expiryWarning}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-center space-x-2">
                                    <PermissionGuard permission="contracts.view">
                                      <button
                                        onClick={() => openContractDetails(contract)}
                                        className="p-1.5 text-primary-600 dark:text-accent-400 hover:text-primary-800 dark:hover:text-accent-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-md transition-colors duration-150"
                                        title="Open Workflow"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                    </PermissionGuard>
                                    <PermissionGuard permission="contracts.view">
                                      <button
                                        onClick={(e) => openContractModal(contract, e)}
                                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors duration-150"
                                        title="View Contract Info & Negotiation Email"
                                      >
                                        <Info className="w-4 h-4" />
                                      </button>
                                    </PermissionGuard>
                                    <PermissionGuard permission="contracts.edit">
                                      <button
                                        onClick={() => {
                                          setEditingContract(contract);
                                          setContractFormData({
                                            supplierId: contract.supplierId,
                                            contractTitle: contract.contractTitle,
                                            year: contract.year,
                                            startDate: formatDateForInput(contract.startDate),
                                            endDate: formatDateForInput(contract.endDate),
                                            status: contract.status,
                                            reviewStatus: contract.reviewStatus,
                                            description: contract.description || '',
                                            paymentTerms: contract.paymentTerms || '',
                                            renewalDate: formatDateForInput(contract.renewalDate || ''),
                                            attachments: contract.attachments || [],
                                            negotiationPoints: contract.negotiationPoints,
                                            // New fields
                                            minPurchaseAmount: (contract as any).minPurchaseAmount || undefined,
                                            marketingBudget: (contract as any).marketingBudget || undefined,
                                            renewalTermsCount: (contract as any).renewalTermsCount || undefined,
                                            incotermTerms: (contract as any).incotermTerms || '',
                                            paymentCurrency: (contract as any).paymentCurrency || '',
                                            groupId: contract.groupId || undefined,
                                            category: (contract.category ?? null) as ContractCategory | null
                                          });
                                          setShowContractModal(true);
                                        }}
                                        className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-md transition-colors duration-150"
                                        title="Edit"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                    </PermissionGuard>
                                    <PermissionGuard permission="contracts.delete">
                                      <button
                                        onClick={() => {
                                          if (window.confirm(`Are you sure you want to delete "${decodeHtmlEntities(contract.contractTitle)}"?`)) {
                                            handleDeleteContract(contract.id);
                                          }
                                        }}
                                        className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors duration-150"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </PermissionGuard>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {filteredContracts.length === 0 && (
                      <div className="text-left py-12">
                        <p className="text-gray-500">No contracts found matching your filters.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'repository' && <ContractRepository onContractsChanged={loadData} />}

              {activeTab === 'contract-review' && (
                <ContractReview
                  contracts={contracts.map(c => ({...c, contractTitle: c.contractTitle, supplier: c.supplierName, value: 0, category: 'General', favorabilityRating: c.favorabilityRating}))}
                  reviewClauses={reviewClauses}
                  negotiationPoints={negotiationPoints}
                  currentUser={currentUser}
                  initialContractId={selectedContract?.id}
                  key={`contract-review-${selectedContract?.id || 'none'}`}
                  onAnalysisUpload={async (contractId: string, file: File) => { await handleAnalysisUpload({}, contractId, file); }}
                  onSummaryUpdate={handleSummaryUpdate}
                  onAddClause={handleAddClause}
                  onUpdateClause={handleUpdateClause}
                  onDeleteClause={handleDeleteClause}
                  onAddNegotiationPoint={handleAddNegotiationPoint}
                  onUpdateNegotiationPoint={handleUpdateNegotiationPoint}
                  onDeleteNegotiationPoint={handleDeleteNegotiationPoint}
                />
              )}
              {activeTab === 'rule-book' && <RuleBook />}
              {activeTab === 'master-data' && (
                <div className="space-y-6">
                  <MasterData
                    suppliers={suppliers}
                    contracts={contracts}
                    contractTypes={[]}
                    onAddSupplier={() => setShowSupplierModal(true)}
                    onEditSupplier={(supplier) => {
                      // Ensure the supplier has all required fields for editing
                      const fullSupplier: Supplier = {
                        ...supplier,
                        address: supplier.address ? (() => {
                          // Handle both string and object address formats
                          if (typeof supplier.address === 'string') {
                            // Parse string format: "street, city, state, zipCode, country"
                            const parts = (supplier.address as string).split(',').map((p: string) => p.trim());
                            return {
                              street: parts[0] || '',
                              city: parts[1] || '',
                              state: parts[2] || '',
                              zipCode: parts[3] || '',
                              country: parts[4] || 'United States'
                            };
                          } else {
                            // Handle object format
                            return {
                              street: supplier.address.street || '',
                              city: supplier.address.city || '',
                              state: supplier.address.state || '',
                              zipCode: supplier.address.zipCode || '',
                              country: supplier.address.country || 'United States'
                            };
                          }
                        })() : {
                          street: '',
                          city: '',
                          state: '',
                          zipCode: '',
                          country: 'United States'
                        },
                        contactPersons: supplier.contactPersons || [{
                          name: supplier.contactPerson || '',
                          role: 'Primary Contact',
                          email: supplier.email || '',
                          phone: supplier.phone || '',
                          isPrimary: true
                        }],
                        category: supplier.category || 'Technology',
                        status: supplier.status || 'active'
                      };
                      
                      setEditingSupplier(fullSupplier);
                      setSupplierForm(fullSupplier);
                      setShowSupplierModal(true);
                    }}
                    onDeleteSupplier={(id) => {
                      handleDeleteSupplier(id);
                    }}
                    onUpdateContractTypes={() => {}}
                  />
                </div>
              )}
            </main>
          </div>

          {/* Modals */}
          {/* Contract Form Modal */}
          {showContractModal && (
            <div className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-dark-border shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {editingContract ? 'Edit Contract' : 'New Contract'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowContractModal(false);
                      setEditingContract(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded-lg transition-colors text-gray-500 dark:text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSaveContract(); }} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contract Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={contractFormData.contractTitle}
                        onChange={(e) => setContractFormData({...contractFormData, contractTitle: e.target.value})}
                        required
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Supplier <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={contractFormData.supplierId}
                        onChange={(e) => setContractFormData({...contractFormData, supplierId: e.target.value})}
                        required
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      >
                        <option value="">Select Supplier</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Group
                      </label>
                      <select
                        value={contractFormData.groupId || ''}
                        onChange={(e) => setContractFormData({...contractFormData, groupId: e.target.value || undefined})}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      >
                        <option value="">No Group</option>
                        {contractGroups.map(group => (
                          <option key={group.id || group._id} value={group.id || group._id}>{group.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contract-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <select
                        id="contract-category"
                        value={contractFormData.category || ''}
                        onChange={(e) => setContractFormData({...contractFormData, category: (e.target.value || null) as ContractCategory | null})}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      >
                        <option value="">Not set</option>
                        {CONTRACT_CATEGORIES.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={contractFormData.year}
                        onChange={(e) => setContractFormData({...contractFormData, year: parseInt(e.target.value)})}
                        required
                        min="2020"
                        max="2030"
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={contractFormData.startDate}
                        onChange={(e) => setContractFormData({...contractFormData, startDate: e.target.value})}
                        required
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={contractFormData.endDate}
                        onChange={(e) => setContractFormData({...contractFormData, endDate: e.target.value})}
                        required
                        min={contractFormData.startDate}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={contractFormData.status}
                        onChange={(e) => setContractFormData({...contractFormData, status: e.target.value as any})}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="terminated">Terminated</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Review Status</label>
                      <select
                        value={contractFormData.reviewStatus}
                        onChange={(e) => setContractFormData({...contractFormData, reviewStatus: e.target.value as any})}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      >
                        <option value="not_received">Not Received</option>
                        <option value="pending_review">Pending Review</option>
                        <option value="under_review">Under Review</option>
                        <option value="finalized">Finalized</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      value={contractFormData.description}
                      onChange={(e) => setContractFormData({...contractFormData, description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>

                  {/* New fields section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Purchase Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={contractFormData.minPurchaseAmount || ''}
                        onChange={(e) => setContractFormData({
                          ...contractFormData,
                          minPurchaseAmount: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Marketing Budget</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={contractFormData.marketingBudget || ''}
                        onChange={(e) => setContractFormData({
                          ...contractFormData,
                          marketingBudget: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Renewal Terms (count)</label>
                      <input
                        type="number"
                        min="1"
                        value={contractFormData.renewalTermsCount || ''}
                        onChange={(e) => setContractFormData({
                          ...contractFormData,
                          renewalTermsCount: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Currency</label>
                      <input
                        type="text"
                        maxLength={10}
                        placeholder="e.g., USD, EUR"
                        value={contractFormData.paymentCurrency || ''}
                        onChange={(e) => setContractFormData({
                          ...contractFormData,
                          paymentCurrency: e.target.value.toUpperCase()
                        })}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Incoterm Terms</label>
                    <textarea
                      value={contractFormData.incotermTerms || ''}
                      onChange={(e) => setContractFormData({...contractFormData, incotermTerms: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter incoterm terms..."
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowContractModal(false);
                        setEditingContract(null);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-primary-700 dark:hover:bg-accent-400 transition-colors font-medium"
                    >
                      {editingContract ? 'Update' : 'Create'} Contract
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Supplier Form Modal */}
          {showSupplierModal && (
            <div className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-dark-border shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {editingSupplier ? 'Edit Supplier' : 'New Supplier'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowSupplierModal(false);
                      setEditingSupplier(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded-lg transition-colors text-gray-500 dark:text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSaveSupplier(); }} className="space-y-6">
                  {/* Company Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Company Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.companyName}
                          onChange={(e) => setSupplierForm({...supplierForm, companyName: e.target.value})}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Tax ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.taxId}
                          onChange={(e) => setSupplierForm({...supplierForm, taxId: e.target.value})}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Registration Number</label>
                      <input
                        type="text"
                        value={supplierForm.registrationNumber || ''}
                        onChange={(e) => setSupplierForm({...supplierForm, registrationNumber: e.target.value})}
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Address</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Street Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={supplierForm.address.street}
                        onChange={(e) => setSupplierForm({
                          ...supplierForm,
                          address: { ...supplierForm.address, street: e.target.value }
                        })}
                        required
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.address.city}
                          onChange={(e) => setSupplierForm({
                            ...supplierForm,
                            address: { ...supplierForm.address, city: e.target.value }
                          })}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          State/Province <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.address.state}
                          onChange={(e) => setSupplierForm({
                            ...supplierForm,
                            address: { ...supplierForm.address, state: e.target.value }
                          })}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          ZIP/Postal Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.address.zipCode}
                          onChange={(e) => setSupplierForm({
                            ...supplierForm,
                            address: { ...supplierForm.address, zipCode: e.target.value }
                          })}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={supplierForm.address.country}
                        onChange={(e) => setSupplierForm({
                          ...supplierForm,
                          address: { ...supplierForm.address, country: e.target.value }
                        })}
                        required
                        className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                      >
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Germany">Germany</option>
                        <option value="France">France</option>
                        <option value="Australia">Australia</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Primary Contact Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Primary Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Contact Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.contactPersons[0]?.name || ''}
                          onChange={(e) => {
                            const newContactPersons = [...supplierForm.contactPersons];
                            newContactPersons[0] = { ...newContactPersons[0], name: e.target.value };
                            setSupplierForm({ ...supplierForm, contactPersons: newContactPersons });
                          }}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Role/Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={supplierForm.contactPersons[0]?.role || ''}
                          onChange={(e) => {
                            const newContactPersons = [...supplierForm.contactPersons];
                            newContactPersons[0] = { ...newContactPersons[0], role: e.target.value };
                            setSupplierForm({ ...supplierForm, contactPersons: newContactPersons });
                          }}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={supplierForm.contactPersons[0]?.email || ''}
                          onChange={(e) => {
                            const newContactPersons = [...supplierForm.contactPersons];
                            newContactPersons[0] = { ...newContactPersons[0], email: e.target.value };
                            setSupplierForm({ ...supplierForm, contactPersons: newContactPersons });
                          }}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={supplierForm.contactPersons[0]?.phone || ''}
                          onChange={(e) => {
                            const newContactPersons = [...supplierForm.contactPersons];
                            newContactPersons[0] = { ...newContactPersons[0], phone: e.target.value };
                            setSupplierForm({ ...supplierForm, contactPersons: newContactPersons });
                          }}
                          required
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Additional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                        <select
                          value={supplierForm.category}
                          onChange={(e) => setSupplierForm({...supplierForm, category: e.target.value})}
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        >
                          <option value="Technology">Technology</option>
                          <option value="Logistics">Logistics</option>
                          <option value="Legal">Legal</option>
                          <option value="Marketing">Marketing</option>
                          <option value="Manufacturing">Manufacturing</option>
                          <option value="Consulting">Consulting</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                        <select
                          value={supplierForm.status}
                          onChange={(e) => setSupplierForm({...supplierForm, status: e.target.value as any})}
                          className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSupplierModal(false);
                        setEditingSupplier(null);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-primary-700 dark:hover:bg-accent-400 transition-colors font-medium"
                    >
                      {editingSupplier ? 'Update' : 'Create'} Supplier
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Bulk Delete Confirmation Modal */}
          {showBulkDeleteConfirm && (
            <div className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-dark-surface rounded-2xl p-6 w-full max-w-md border border-gray-200 dark:border-dark-border shadow-xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-error-100 dark:bg-error-900/30 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-error-600 dark:text-error-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Contracts</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Are you sure you want to delete {selectedContracts.size} selected contracts?
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  This action cannot be undone. All selected contracts and their associated data will be permanently removed.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-error-600 dark:bg-error-500 text-white rounded-lg hover:bg-error-700 dark:hover:bg-error-600 transition-colors font-medium"
                  >
                    Delete Contracts
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contract Details Modal */}
          {isModalOpen && selectedContract && (
            <ContractDetailsModal
              isOpen={isModalOpen}
              contract={{
                id: selectedContract.id.toString(),
                _id: selectedContract._id, // Add missing _id field for file operations
                contractNumber: `CONTRACT-${selectedContract.id}`,
                title: selectedContract.contractTitle,
                description: selectedContract.description,
                supplier: {
                  id: selectedContract.supplierId.toString(),
                  name: selectedContract.supplierName
                },
                startDate: new Date(selectedContract.startDate),
                endDate: new Date(selectedContract.endDate),
                value: 0,
                currency: 'USD',
                status: selectedContract.status,
                category: 'General',
                createdBy: {
                  id: '1',
                  name: currentUser
                },
                createdAt: new Date(),
                updatedAt: new Date(),
                negotiationPoints: selectedContract.negotiationPoints?.map(np => ({
                  id: np.id,
                  title: np.title,
                  description: np.description,
                  status: np.status === 'compromised' ? 'pending' : np.status as 'pending' | 'agreed' | 'rejected',
                  priority: np.priority as 'high' | 'medium' | 'low'
                })),
                files: selectedContract.attachments?.map(att => ({
                  id: att.id,
                  name: att.fileName,
                  filename: att.fileName,
                  size: att.fileSize,
                  fileSize: att.fileSize,
                  type: att.fileType || 'application/octet-stream',
                  uploadDate: new Date(att.uploadDate),
                  uploadedAt: new Date(att.uploadDate),
                  uploadedBy: {
                    id: '1',
                    name: att.uploadedBy,
                    email: ''
                  },
                  url: '',
                  downloadUrl: '',
                  mimeType: att.fileType || 'application/octet-stream',
                  version: '1.0'
                })) || [],
                // Include the new fields
                minPurchaseAmount: selectedContract.minPurchaseAmount,
                marketingBudget: selectedContract.marketingBudget,
                renewalTermsCount: selectedContract.renewalTermsCount,
                incotermTerms: selectedContract.incotermTerms,
                paymentCurrency: selectedContract.paymentCurrency,
                // Include negotiation email if available
                negotiationEmail: selectedContract.negotiationEmail
              }}
              onClose={closeContractDetails}
              onEdit={(contract) => {
                const contractToEdit = contracts.find(c => c.id === contract.id);
                if (contractToEdit) {
                  setEditingContract(contractToEdit);
                  setContractFormData({
                    supplierId: contractToEdit.supplierId,
                    contractTitle: contractToEdit.contractTitle,
                    year: contractToEdit.year,
                    startDate: formatDateForInput(contractToEdit.startDate),
                    endDate: formatDateForInput(contractToEdit.endDate),
                    status: contractToEdit.status,
                    reviewStatus: contractToEdit.reviewStatus,
                    description: contractToEdit.description || '',
                    paymentTerms: contractToEdit.paymentTerms || '',
                    renewalDate: formatDateForInput(contractToEdit.renewalDate || ''),
                    attachments: contractToEdit.attachments || [],
                    negotiationPoints: contractToEdit.negotiationPoints,
                    // New fields
                    minPurchaseAmount: (contractToEdit as any).minPurchaseAmount || undefined,
                    marketingBudget: (contractToEdit as any).marketingBudget || undefined,
                    renewalTermsCount: (contractToEdit as any).renewalTermsCount || undefined,
                    incotermTerms: (contractToEdit as any).incotermTerms || '',
                    paymentCurrency: (contractToEdit as any).paymentCurrency || '',
                    category: (contractToEdit.category ?? null) as ContractCategory | null
                  });
                  setShowContractModal(true);
                  closeContractDetails();
                }
              }}
              onFileUpload={async (contractId: string, file: File, versionInfo: any) => {
                if (!token) {
                  showError('Authentication required');
                  return;
                }
                
                try {
                  // Find the contract to get the proper ObjectId
                  const contract = contracts.find(c => c.id.toString() === contractId);
                  const actualContractId = contract?._id || contractId;
                  
                  // Validate that we have a proper ObjectId
                  if (!isValidObjectId(actualContractId)) {
                    showError('Cannot upload file: Invalid contract ID. This contract may not exist in the database.');
                    return;
                  }
                  
                  await contractService.uploadFile(token, actualContractId, file, versionInfo);
                  showSuccess('File uploaded successfully');
                  // Refresh contract data to show the new file
                  await loadData();
                } catch (error) {
                  console.error('File upload failed:', error);
                  showError('Failed to upload file');
                  throw error;
                }
              }}
              onFileDelete={async (contractId: string, fileId: string) => {
                if (!token) {
                  showError('Authentication required');
                  return;
                }
                
                try {
                  // Find the contract by checking both numeric ID and ObjectId
                  const contract = contracts.find(c => 
                    c.id.toString() === contractId || 
                    c._id === contractId
                  );
                  const actualContractId = contract?._id || contractId;
                  
                  // Validate that we have a proper ObjectId
                  if (!isValidObjectId(actualContractId)) {
                    showError('Cannot delete file: Invalid contract ID. This contract may not exist in the database.');
                    return;
                  }
                  
                  await contractService.deleteFile(token, actualContractId, fileId);
                  showSuccess('File deleted successfully');
                } catch (error) {
                  console.error('File deletion failed:', error);
                  showError('Failed to delete file');
                  throw error;
                }
              }}
              onNoteAdd={async (contractId: string, content: string) => {
                if (!token) {
                  showError('Authentication required');
                  return;
                }
                
                try {
                  // Find the contract to get the proper ObjectId
                  const contract = contracts.find(c => c.id.toString() === contractId);
                  const actualContractId = contract?._id || contractId;
                  
                  // Validate that we have a proper ObjectId
                  if (!isValidObjectId(actualContractId)) {
                    showError('Cannot add note: Invalid contract ID. This contract may not exist in the database.');
                    return;
                  }
                  
                  await contractService.addNote(token, actualContractId, { content });
                  showSuccess('Note added successfully');
                  // Optionally refresh contract data to show the new note
                } catch (error) {
                  console.error('Failed to add note:', error);
                  showError('Failed to add note');
                  throw error;
                }
              }}
              onNoteEdit={async (contractId: string, noteId: string, content: string) => {
                if (!token) {
                  showError('Authentication required');
                  return;
                }
                
                try {
                  // Find the contract to get the proper ObjectId
                  const contract = contracts.find(c => c.id.toString() === contractId);
                  const actualContractId = contract?._id || contractId;
                  
                  // Validate that we have a proper ObjectId
                  if (!isValidObjectId(actualContractId)) {
                    showError('Cannot edit note: Invalid contract ID. This contract may not exist in the database.');
                    return;
                  }
                  
                  await contractService.editNote(token, actualContractId, noteId, { content });
                  showSuccess('Note updated successfully');
                  // Optionally refresh contract data to show the updated note
                } catch (error) {
                  console.error('Failed to edit note:', error);
                  showError('Failed to edit note');
                  throw error;
                }
              }}
              onNoteDelete={async (contractId: string, noteId: string) => {
                if (!token) {
                  showError('Authentication required');
                  return;
                }
                
                try {
                  // Find the contract to get the proper ObjectId
                  const contract = contracts.find(c => c.id.toString() === contractId);
                  const actualContractId = contract?._id || contractId;
                  
                  // Validate that we have a proper ObjectId
                  if (!isValidObjectId(actualContractId)) {
                    showError('Cannot delete note: Invalid contract ID. This contract may not exist in the database.');
                    return;
                  }
                  
                  await contractService.deleteNote(token, actualContractId, noteId);
                  showSuccess('Note deleted successfully');
                  // Optionally refresh contract data to remove the deleted note
                } catch (error) {
                  console.error('Failed to delete note:', error);
                  showError('Failed to delete note');
                  throw error;
                }
              }}
              onReview={(contract) => {
                if (selectedContract) {
                  setActiveTab('contract-review');
                  setIsModalOpen(false);
                }
              }}
            />
          )}
        </div>
      } />
    </Routes>
  );
};

export default ContractManager;
